import { useSearchStore } from '../stores/searchStore.js';
import { apiClientInstance } from './apiClient.js';
import { appConfig } from '../config/appConfig.js';

/**
 * SearchService - Orchestrates search operations between UI, state, and API
 * Provides centralized search management and coordinates with map/filter systems
 * Follows the same pattern as FilterManager
 */
export class SearchService {
  constructor() {
    this.apiClient = apiClientInstance;
    this.isSearching = false;
    this.debounceTimeout = null;
    this.lastSearchTime = null;

    // Callbacks for integration with other services
    this.onSearchExecuted = null;
    this.onResultSelected = null;
    this.onSearchCleared = null;
  }

  /**
   * Execute a search with debouncing
   * @param {string} query - Search query string
   * @param {boolean} debounce - Whether to debounce the request (default: true)
   */
  async executeSearch(query, debounce = true) {
    // Clear any existing debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    const performSearch = async () => {
      try {
        this.isSearching = true;
        this.lastSearchTime = Date.now();

        const searchStore = useSearchStore.getState();
        searchStore.setQuery(query);
        searchStore.setSearching(true);

        // Validate query
        if (!query || query.length < 2) {
          searchStore.setResults([]);
          searchStore.setSearching(false);
          this.isSearching = false;
          return;
        }

        // Execute parallel search for both mines and countries
        const [mineResponse, countryResponse] = await Promise.all([
          this.apiClient.searchSuggestions(query, { limit: 10 }),
          this.apiClient.searchCountries(query, { limit: 5 })
        ]);

        console.log('SearchService: API responses received', {
          mineResponse: { success: mineResponse.success, count: mineResponse.suggestions?.length },
          countryResponse: { success: countryResponse?.success, count: countryResponse?.data?.results?.length, hasData: !!countryResponse?.data }
        });

        // Merge results: countries first, then mines
        let allResults = [];

        // Country API returns { success: true, data: { results: [...] } }
        if (countryResponse && countryResponse.success && countryResponse.data?.results) {
          console.log('SearchService: Adding country results:', countryResponse.data.results);
          allResults = [...countryResponse.data.results];
        } else {
          console.warn('SearchService: No country results', { success: countryResponse?.success, hasData: !!countryResponse?.data });
        }

        if (mineResponse.success && mineResponse.suggestions) {
          allResults = [...allResults, ...mineResponse.suggestions];
        }

        console.log('SearchService: Search executed successfully', {
          query,
          countryCount: countryResponse?.success ? (countryResponse.data?.results || []).length : 0,
          mineCount: mineResponse.success ? (mineResponse.suggestions || []).length : 0,
          totalResults: allResults.length,
          searchTime: Date.now() - this.lastSearchTime
        });

        // Update store with merged results
        searchStore.setResults(allResults);
        searchStore.addToHistory(query);

        // Notify listeners about search execution
        if (this.onSearchExecuted) {
          this.onSearchExecuted(query, allResults);
        }

        searchStore.setSearching(false);
        this.isSearching = false;

      } catch (error) {
        console.error('SearchService: Search execution failed', error);

        const searchStore = useSearchStore.getState();
        searchStore.setResults([]);
        searchStore.setSearching(false);
        this.isSearching = false;

        throw error;
      }
    };

    if (debounce) {
      this.debounceTimeout = setTimeout(performSearch, appConfig.search.debounceDelay);
    } else {
      await performSearch();
    }
  }

  /**
   * Handle selection of a search result
   * @param {Object} result - Selected search result
   * @param {Object} options - Selection options (centerMap, applyFilters, etc.)
   */
  async selectResult(result, options = {}) {
    try {
      console.log('SearchService: Result selected', result);

      const searchStore = useSearchStore.getState();
      searchStore.setSelectedResult(result);

      // Default options
      const {
        centerMap = true,
        applyFilters = true,
        closeSearch = true,
        zoomLevel = null
      } = options;

      // Notify listeners about result selection
      if (this.onResultSelected) {
        await this.onResultSelected(result, {
          centerMap,
          applyFilters,
          closeSearch,
          zoomLevel
        });
      }

      // Close search if requested
      if (closeSearch) {
        this.closeSearch();
      }

    } catch (error) {
      console.error('SearchService: Result selection failed', error);
      throw error;
    }
  }

  /**
   * Clear current search
   */
  clearSearch() {
    try {
      const searchStore = useSearchStore.getState();
      searchStore.resetSearch();

      // Clear any pending debounce
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      this.isSearching = false;

      // Notify listeners about search clear
      if (this.onSearchCleared) {
        this.onSearchCleared();
      }

      console.log('SearchService: Search cleared');
    } catch (error) {
      console.error('SearchService: Clear search failed', error);
    }
  }

  /**
   * Show search interface
   */
  showSearch() {
    const searchStore = useSearchStore.getState();
    searchStore.setVisible(true);
  }

  /**
   * Hide search interface
   */
  closeSearch() {
    const searchStore = useSearchStore.getState();
    searchStore.setVisible(false);
  }

  /**
   * Toggle search interface visibility
   */
  toggleSearch() {
    const searchStore = useSearchStore.getState();
    const isVisible = searchStore.getSearchState().isVisible;

    if (isVisible) {
      this.closeSearch();
    } else {
      this.showSearch();
    }
  }

  /**
   * Get search suggestions from history
   * @param {string} query - Partial query to match against history
   * @returns {Array} - Matching search history items
   */
  getHistorySuggestions(query = '') {
    const searchStore = useSearchStore.getState();
    const history = searchStore.searchHistory || [];

    if (!query) {
      return history.slice(0, 5); // Return recent 5 if no query
    }

    return history.filter(item =>
      item.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5);
  }

  /**
   * Register callbacks for integration with other services
   */
  setCallbacks({ onSearchExecuted, onResultSelected, onSearchCleared }) {
    this.onSearchExecuted = onSearchExecuted;
    this.onResultSelected = onResultSelected;
    this.onSearchCleared = onSearchCleared;
  }

  /**
   * Get current search state
   */
  getSearchState() {
    const searchStore = useSearchStore.getState();
    return searchStore.getSearchState();
  }

  /**
   * Check if currently searching
   */
  isCurrentlySearching() {
    return this.isSearching;
  }

  /**
   * Cleanup method
   */
  destroy() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.onSearchExecuted = null;
    this.onResultSelected = null;
    this.onSearchCleared = null;
  }
}

// Create and export singleton instance
export const searchService = new SearchService();